import { RowCrudStatus } from "../../common/types";
import {
  AuthorCollectingSociety,
  AuthorCollectingSociety_CreateInput,
  AuthorCollectingSociety_UpdateInput,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { collectiveManagementSocietyDisplayId } from "../../collectiveManagementSociety/store/CollectiveManagementSocietyCommonStore";
import { territoryDisplayId } from "../../territory/store/TerritoryCommonStore";

export type AuthorCollectingSocietyValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  __collectingSociety: string;
  showCollectingSociety: { displayId: string; id: string } | null;
  memberNumber: string;
  __territories: string[];
  showTerritories: { displayId: string; id: string }[];
};

export type AuthorCollectingSocietyErrorsType = {
  collectingSociety?: { message: string; fieldLabel: string };
  memberNumber?: { message: string; fieldLabel: string };
  territories?: { message: string; fieldLabel: string };
};

export function AuthorCollectingSocietyValuesInit(): AuthorCollectingSocietyValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    __collectingSociety: "",
    showCollectingSociety: null,
    memberNumber: "",
    __territories: [],
    showTerritories: [],
  };
}

export type AuthorCollectingSocietyType =
  | AuthorCollectingSociety
  | AuthorCollectingSocietyValuesType;

export function authorCollectingSocietyDisplayId(
  source: Partial<AuthorCollectingSociety>,
  _langStore: LangStore
) {
  return (
    (source.collectingSociety
      ? collectiveManagementSocietyDisplayId(
          source.collectingSociety,
          _langStore
        )
      : _langStore.emptyStringSymbol) +
    ": " +
    (source.memberNumber ?? _langStore.emptyStringSymbol)
  );
}

export function getAuthorCollectingSocietyValues(
  source: AuthorCollectingSocietyType,
  _langStore: LangStore,
  _duplicated: boolean = false
): AuthorCollectingSocietyValuesType {
  const entityValues = AuthorCollectingSocietyValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : authorCollectingSocietyDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.showCollectingSociety =
    "collectingSociety" in source && source.collectingSociety
      ? {
          id: source.collectingSociety.id,
          displayId: collectiveManagementSocietyDisplayId(
            source.collectingSociety,
            _langStore
          ),
        }
      : "showCollectingSociety" in source && source.showCollectingSociety
      ? {
          id: source.showCollectingSociety.id,
          displayId: source.showCollectingSociety.displayId,
        }
      : null;
  entityValues.__collectingSociety =
    entityValues.showCollectingSociety?.displayId || "";
  entityValues.memberNumber = source.memberNumber;
  entityValues.showTerritories =
    "territories" in source && source.territories
      ? (source.territories || []).map((x) => {
          return { id: x.id, displayId: territoryDisplayId(x, _langStore) };
        })
      : "showTerritories" in source && source.showTerritories
      ? (source.showTerritories || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__territories = entityValues.showTerritories.map(
    (x) => x.displayId
  );
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getAuthorCollectingSocietyUpdateInput(
  values: AuthorCollectingSocietyValuesType
): AuthorCollectingSociety_UpdateInput {
  return {
    id: values.id,
    version_: values.version_,
    collectingSocietyId: values.showCollectingSociety?.id || "",
    memberNumber: values.memberNumber,
    territoriesIds: values.showTerritories
      ? { set: values.showTerritories.map((x) => x.id) }
      : undefined,
  };
}

export function getAuthorCollectingSocietyCreateInput(
  values: AuthorCollectingSocietyValuesType
): AuthorCollectingSociety_CreateInput {
  return {
    collectingSocietyId: values.showCollectingSociety?.id || "",
    memberNumber: values.memberNumber,
    territoriesIds: values.showTerritories.map((x) => x.id),
  };
}
